import React, { useCallback, useEffect, useState } from "react";
import {
  GameOverType,
  GTVMessage,
  PlayerType,
  ThemeType,
} from "../../shared/util/types";
import {
  AllCaptionsInType,
  AllGuessesInType,
  GetPromptsType,
  NewImageType,
} from "./types";
import CaptionsList from "../../shared/components/CaptionsList";
import CaptionPresentation from "./CaptionPresentation";
import ShowScores from "../../shared/components/ShowScores";
import { Box } from "@mantine/core";
import DynamicImage from "../../shared/components/DynamicImage";
import DynamicText from "../../shared/components/DynamicText";
import PlayerBadges from "../../shared/components/PlayerBadges";
import PlayerReminder from "../../shared/components/PlayerReminder";
import { DEFAULT_THEME } from "../../shared/util/consts";
import TransitionContainer from "../../shared/components/TransitionContainer";
import { motion } from "framer-motion";

type ArtificialGameProps = {
  sendHostMessage: (msg: string) => Promise<void>;
  gameMessage: GTVMessage | null;
  playMedia: (mediaUrl: string) => void;
};

const ArtificialGame = ({
  sendHostMessage,
  gameMessage,
  playMedia,
}: ArtificialGameProps) => {
  const [players, setPlayers] = useState<PlayerType[]>([]);
  const [judge, setJudge] = useState("");
  const [round, setRound] = useState("Round 1");
  const [theme, setTheme] = useState<ThemeType>(DEFAULT_THEME);
  const [instructions, setInstructions] = useState("");
  const [reminder, setReminder] = useState("");
  const [state, setState] = useState("GetPrompts"); // Should be GetPrompts to start
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [captions, setCaptions] = useState<string[]>([]);
  const [results, setResults] = useState<AllGuessesInType | undefined>(
    undefined,
  );
  const [responseMessage, setResponseMessage] = useState("");
  const handleMessage = useCallback(
    async (msg: GTVMessage) => {
      console.log("Artificial Processing msg", msg);
      if (!msg) {
        return false;
      }
      if (msg.Theme) {
        setTheme(msg.Theme as ThemeType);
      }
      switch (msg.Type) {
        case "AddPlayer":
          const newPlayer = msg.Payload as PlayerType;
          !players.find((p) => p.Name === newPlayer.Name) &&
            setPlayers([...players, { ...newPlayer, Submitted: true }]);
          break;
        case "GetPrompts":
          const getPrompts: GetPromptsType = msg.Payload as GetPromptsType;
          setImageUrl(undefined);
          setPlayers(
            getPrompts.Players.map((p) => {
              return {
                Name: p,
                Submitted: false,
              };
            }),
          );
          setJudge("");
          setState(msg.Type);
          setRound("Round " + getPrompts.Round);
          setInstructions(getPrompts.Instructions);
          setReminder(getPrompts.Reminder);
          setResponseMessage(getPrompts.ResponseMessage);
          break;
        case "OnePlayerIn":
          playMedia("/drop2.mp3");
          setPlayers((prev) =>
            prev.map((p) => {
              return {
                ...p,
                Submitted:
                  p.Name === (msg.Payload as string) ? true : p.Submitted,
              };
            }),
          );
          break;
        case "NewImage":
          setState(msg.Type);
          const newImage: NewImageType = msg.Payload as NewImageType;
          setRound("Round " + newImage.Round);
          setImageUrl(newImage.ImageUrl);
          setJudge(newImage.Judge);
          setInstructions(newImage.Instructions);
          setReminder(newImage.Reminder);
          setPlayers(
            newImage.Players.map((p) => {
              return {
                Name: p,
                Submitted: p === newImage.Judge,
              };
            }),
          );
          setResponseMessage(newImage.ResponseMessage);
          break;
        case "AllCaptionsIn":
          setState(msg.Type);
          const captions: AllCaptionsInType = msg.Payload as AllCaptionsInType;
          setCaptions(captions.Choices);
          setImageUrl(captions.ImageUrl);
          setJudge(captions.Judge);
          setInstructions(captions.Instructions);
          setReminder(captions.Reminder);
          setPlayers(
            captions.Players.map((p) => {
              return {
                Name: p,
                Submitted: p === captions.Judge,
              };
            }),
          );
          setResponseMessage(captions.ResponseMessage);
          break;
        case "AllGuessesIn":
          setState(msg.Type);
          setInstructions("");
          setReminder("");
          const results: AllGuessesInType = msg.Payload as AllGuessesInType;
          setResults(results);
          setPlayers(
            results.PlayerPoints.map((p) => {
              return {
                Name: p.PlayerName,
                Score: p.Points,
                Submitted: true,
              };
            }),
          );
          setResponseMessage(results.ResponseMessage);
          break;
        case "GameOver":
          playMedia("/gameOver.mp3");
          setRound("Game Over!");
          const gameOver: GameOverType = msg.Payload as GameOverType;
          setInstructions(gameOver.Instructions);
          setReminder("Want to play again?");
          setState(msg.Type);
          setPlayers(
            players.map((p) => {
              return {
                ...p,
                Score: gameOver.Points[p.Name],
              };
            }),
          );
          break;
        default:
          console.log("Unimplemented message:", msg);
          return false;
      }
      return true;
    },
    [
      players,
      setPlayers,
      setImageUrl,
      setState,
      setRound,
      setInstructions,
      setReminder,
      setCaptions,
      setResults,
      playMedia,
    ],
  );
  useEffect(() => {
    gameMessage && handleMessage(gameMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameMessage]);
  const imageVisible =
    state === "NewImage" ||
    state === "AllCaptionsIn" ||
    state === "AllGuessesIn";
  const getPlayerInfo: PlayerType[] = players.map((p) => {
    return {
      Name: p.Name,
      Score: p.Score,
      Caption: results
        ? results.Prompt.Truth.PlayerName === p.Name
          ? results.Prompt.Truth.Text
          : results.Prompt.Captions.find((c) => {
              return c.PlayerName === p.Name;
            })?.Text
        : undefined,
    };
  });
  const instructionsNewLines = instructions.split("\n");
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        margin: "50px",
        alignItems: "center",
      }}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          margin: "10px",
          height: "100%",
          width: "100%",
        }}
      >
        {imageVisible && (
          <Box
            id="image-container"
            style={{
              display: "flex",
              width: "50%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <DynamicImage src={imageUrl} parentId={"image-container"} />
          </Box>
        )}
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            width: imageVisible ? "50%" : "100%",
            justifyContent: "space-between",
            height: "100%",
            margin: "10px",
          }}
        >
          <DynamicText
            style={{
              color: theme.ButtonColor,
              fontSize: 15,
              margin: 10,
              width: "100%",
              justifyContent: "center",
            }}
          >
            {round}
          </DynamicText>
          <TransitionContainer transitionTrigger={instructions}>
            {state === "GetPrompts" || state === "NewImage" ? (
              instructionsNewLines.map((line, i) => (
                <motion.div
                  key={"instruction" + i + line}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 1, delay: i * 0.2 }}
                  onAnimationComplete={async () => {
                    if (i >= instructionsNewLines.length - 1) {
                      await sendHostMessage(responseMessage);
                      setResponseMessage("");
                    }
                  }}
                >
                  <DynamicText
                    key={i}
                    style={{
                      fontSize: 34,
                      textAlign: "center",
                      width: "100%",
                    }}
                  >
                    {line.trim()}
                  </DynamicText>
                </motion.div>
              ))
            ) : state === "AllCaptionsIn" ? (
              <CaptionsList
                theme={theme}
                captions={captions}
                onRenderComplete={async () => {
                  await sendHostMessage(responseMessage);
                  setResponseMessage("");
                }}
              />
            ) : state === "AllGuessesIn" ? (
              <CaptionPresentation
                theme={theme}
                results={results}
                onNewState={(state: string) => {
                  setImageUrl(undefined);
                  setState(state);
                  setReminder("");
                }}
                setReminder={setReminder}
              />
            ) : state === "ShowScores" || state === "GameOver" ? (
              <ShowScores
                reminder={reminder}
                onRenderComplete={async () => {
                  await sendHostMessage(responseMessage);
                  setResponseMessage("");
                }}
                players={getPlayerInfo}
              />
            ) : null}
          </TransitionContainer>
          {players && state !== "GameOver" && state !== "ShowScores" && (
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100px",
                justifyContent: "space-between",
              }}
            >
              <PlayerReminder reminder={reminder} theme={theme} />
              <PlayerBadges
                players={players}
                judge={judge}
                theme={theme}
                allowTwoRows={state !== "GetPrompts"}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ArtificialGame;
